import React from 'react'
import { useTheme } from '@emotion/react'

import {
    StyledSectionHeaderWrapper,
    StyledHeading,
    StyledHeadingLink,
    StyledListItem,
    StyledLink,
    StyledList,
    StyledMastheadLink,
    StyledImageContainer,
    StyledIconClock,
    StyledChevron,
    StyledSectionHeader,
    StyledMastheadContainer,
} from 'component-library/section-header/SectionHeader/SectionHeader.styled'
import {
    StyledSponsoredSectionHeadingContainer,
    StyledSponsoredHeading,
} from 'component-library/section-header/SectionHeader/Styled/SponsoredHeading.styled'
import {
    Props as SponsoredTopicProps,
    SponsoredTopic,
} from 'component-library/advertising/SponsoredTopic/SponsoredTopic'

import { Section } from 'component-library/__styling/settings/sections'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { ThemedRender } from 'component-library/__styling/themed-render'
import { SectionHeaderLogo } from './SectionHeaderLogo/SectionHeaderLogo'

export interface NavLink {
    text: string
    link: string
}

interface SectionHeaderProps {
    hasMargin?: boolean
    heading: string
    headingUrl?: string
    masthead?: NavLink
    navLinks?: NavLink[]
    section?: Section | 'sponsored'
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
    className?: string
    canGrow?: boolean
    fontScale?: FontScales
    showClock?: boolean
    showLogo?: boolean
    hideShowMoreLabel?: boolean
    customSponsorMargin?: string
    hideSponsorOnMobile?: boolean
    hideShowMoreButtonOnMobile?: boolean
    invisibleHeaderBreakpoint?: 'sm' | 'md' // Hides the section header visually but retains the space
}

export type Props = SectionHeaderProps & SponsoredTopicProps

export type InternalProps = Props

export const SectionHeader: React.FC<InternalProps> = ({
    hasMargin,
    className,
    canGrow,
    heading,
    headingUrl,
    masthead,
    sponsor,
    hideSponsorOnMobile,
    customSponsorMargin,
    navLinks,
    section: sectionOverride,
    fontScale,
    onClick,
    showClock,
    showLogo,
    invisibleHeaderBreakpoint,
}) => {
    const theme = useTheme()
    const Icon = showClock ? StyledIconClock : StyledChevron

    if (sectionOverride === 'sponsored') {
        return (
            <StyledSponsoredSectionHeadingContainer>
                <StyledSponsoredHeading>{heading}</StyledSponsoredHeading>
            </StyledSponsoredSectionHeadingContainer>
        )
    }
    return (
        <StyledSectionHeaderWrapper
            sectionOverride={sectionOverride}
            className={className}
            hasMargin={hasMargin}
            canGrow={canGrow}
            sponsor={sponsor}
            masthead={masthead}
            invisibleHeaderBreakpoint={invisibleHeaderBreakpoint}
        >
            <StyledSectionHeader sponsor={sponsor}>
                <ThemedRender
                    sevennews={() =>
                        showLogo ? (
                            <SectionHeaderLogo section={sectionOverride || theme.section} />
                        ) : (
                            <Icon />
                        )
                    }
                />
                {headingUrl ? (
                    <StyledHeadingLink
                        to={headingUrl}
                        openExternalInNewWindow={true}
                        onClick={onClick}
                        rel="noopener"
                    >
                        <StyledHeading isLink={true} fontScale={fontScale} showLogo={showLogo}>
                            {heading}
                        </StyledHeading>
                    </StyledHeadingLink>
                ) : (
                    <StyledHeading isLink={false} fontScale={fontScale} showLogo={showLogo}>
                        {heading}
                    </StyledHeading>
                )}
                {navLinks && (
                    <StyledList>
                        {navLinks.map((item, index) => (
                            <StyledListItem section={theme.section} key={index}>
                                <StyledLink to={item.link} onClick={onClick}>
                                    {item.text}
                                </StyledLink>
                            </StyledListItem>
                        ))}
                    </StyledList>
                )}

                {sponsor && (
                    <StyledImageContainer
                        customMargin={customSponsorMargin}
                        hideOnMobile={hideSponsorOnMobile}
                    >
                        <SponsoredTopic sponsor={sponsor} />
                    </StyledImageContainer>
                )}
            </StyledSectionHeader>

            {masthead && (
                <StyledMastheadContainer>
                    <StyledMastheadLink to={masthead.link} onClick={onClick}>
                        {masthead.text}
                    </StyledMastheadLink>
                </StyledMastheadContainer>
            )}
        </StyledSectionHeaderWrapper>
    )
}
